// src/screens/Resources.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { sanitizeFileName } from "../utils/storage";
import {
  UploadCloud, Trash2, Download, Lock, Folder, File as FileIcon, FileText,
  FileImage, FileAudio2, FileVideo, FileArchive, FileSpreadsheet, FileCode,
  ChevronRight, Home, X, FolderPlus
} from "lucide-react";
import i18n from "../i18n";
import { BackButton } from "../components/BackButton";


type Resource = {
  id: string;
  file_path: string;   // ex: userId/Folder/Sub/file.ext
  file_name: string;
  description?: string;
  created_at: string;
};

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const Resources = () => {
  const { t, lng } = useI18nSafe();
  const locale = lng === "fr" ? "fr-FR" : "en-US";

  const [resources, setResources] = useState<Resource[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Upload avec aperçu
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  // Dossier courant (relatif à userId/)
  const [currentPath, setCurrentPath] = useState<string>("");

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserId(user.id);

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();

      const premium = !!profile?.is_premium;
      setIsPremium(premium);
      if (premium) fetchResources(user.id);
    };
    fetchUser();
  }, []);

  const fetchResources = async (uid: string) => {
    const { data, error } = await supabase
      .from("resources")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false });

    if (!error) setResources(data || []);
  };

  // Icône selon extension
  const renderFileIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase() || "";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return <FileImage className="w-5 h-5 text-emerald-600" />;
    if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return <FileAudio2 className="w-5 h-5 text-amber-600" />;
    if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return <FileVideo className="w-5 h-5 text-indigo-600" />;
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext)) return <FileArchive className="w-5 h-5 text-gray-600" />;
    if (["xls", "xlsx", "csv"].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-700" />;
    if (["doc", "docx", "rtf", "txt", "md", "pdf"].includes(ext)) return <FileText className="w-5 h-5 text-blue-700" />;
    if (["js", "ts", "tsx", "json", "yml", "yaml", "xml", "html", "css"].includes(ext)) return <FileCode className="w-5 h-5 text-slate-700" />;
    return <FileIcon className="w-5 h-5 text-gray-500" />;
  };

  // --------- 👇 TOUS les hooks (useMemo) AVANT tout return 👇 ---------

  // Recherche plein texte
  const filteredResources = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) return resources;
    return resources.filter((res) =>
      `${res.file_name} ${res.description || ""}`.toLowerCase().includes(q)
    );
  }, [resources, searchTerm]);

  // Folders/files du dossier courant (immédiats)
  const { folders, files } = useMemo(() => {
    const folderSet = new Set<string>();
    const filesHere: Resource[] = [];

    const prefix = userId ? `${userId}/` : "";

    filteredResources.forEach((res) => {
      const full = res.file_path;
      const rel = full.startsWith(prefix) ? full.slice(prefix.length) : full; // sans userId/
      if (!rel.startsWith(currentPath)) return;

      const remainder = rel.slice(currentPath.length);
      const parts = remainder.split("/");

      if (parts.length > 1) {
        // sous-dossier immédiat
        folderSet.add(parts[0]);
      } else {
        // fichier immédiat → on exclut le placeholder .keep
        if (res.file_name !== ".keep") {
          filesHere.push(res);
        }
      }
    });

    const folders = Array.from(folderSet).sort((a, b) => a.localeCompare(b));
    const files = filesHere.sort((a, b) => a.file_name.localeCompare(b.file_name));
    return { folders, files };
  }, [filteredResources, currentPath, userId]);

  // Breadcrumb segments
  const crumbs = useMemo(
    () => currentPath.split("/").filter(Boolean),
    [currentPath]
  );

  // --------- 👆 Hooks top-level, pas de return avant 👆 ---------

  // Actions UI
  const normalizeDir = (dir: string) => (dir && !dir.endsWith("/") ? `${dir}/` : dir);
  const enterFolder = (name: string) => setCurrentPath((prev) => normalizeDir(prev + name));
  const goToPath = (segmentsCount: number) => {
    if (segmentsCount <= 0) { setCurrentPath(""); return; }
    const segs = currentPath.split("/").filter(Boolean).slice(0, segmentsCount);
    setCurrentPath(segs.length ? segs.join("/") + "/" : "");
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setSelectedFile(e.target.files[0]);
  };

  const createFolder = async () => {
    if (!userId) return;

    const raw = prompt(t("resources:prompts.folder_name"));
    if (!raw) return;

    // petit nettoyage
    const name = raw.trim().replace(/[/\\]+/g, "-").replace(/\s+/g, " ");
    if (!name) return alert(t("resources:alerts.invalid_name"));
    if (name === "." || name === "..") return alert(t("resources:alerts.invalid_folder_name"));
    if (/[<>:"|?*]/.test(name)) return alert(t("resources:alerts.forbidden_chars"));

    // évite doublons (folders = useMemo)
    if (folders.includes(name)) {
      return alert(t("resources:alerts.folder_exists"));
    }

    // On crée un placeholder ".keep" dans Storage
    const keepPath = `${userId}/${currentPath}${name}/.keep`;
    const emptyBlob = new Blob([""], { type: "text/plain" });

    const { error: upErr } = await supabase
      .storage.from("resources")
      .upload(keepPath, emptyBlob, { upsert: true });

    if (upErr) {
      console.error(upErr);
      return alert(t("resources:errors.create_folder_storage"));
    }

    // Et on insère une ligne en base pour que le dossier vide soit visible
    const { error: insErr } = await supabase.from("resources").insert({
      user_id: userId,
      file_path: keepPath,
      file_name: ".keep",
      description: "",
    });

    if (insErr) {
      console.error(insErr);
      return alert(t("resources:errors.folder_created_not_referenced"));
    }

    await fetchResources(userId);
  };

  const handleUploadSelected = async () => {
    if (!userId || !selectedFile) return;
    setUploading(true);
    try {
      const file = selectedFile;
      const filePath = `${userId}/${currentPath}${sanitizeFileName(file.name)}`;

      const { error: uploadError } = await supabase
        .storage.from("resources")
        .upload(filePath, file, { upsert: true });
      if (uploadError) { alert(t("resources:errors.upload")); return; }

      const description = prompt(t("resources:prompts.file_description")) || "";
      await supabase.from("resources").insert({
        user_id: userId,
        file_path: filePath,
        file_name: file.name,
        description,
      });

      await fetchResources(userId);
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (res: Resource) => {
    if (!window.confirm(t("resources:confirms.delete_file", { name: res.file_name }))) return;
    await supabase.storage.from("resources").remove([res.file_path]);
    await supabase.from("resources").delete().eq("id", res.id);
    if (userId) fetchResources(userId);
  };

  const getSignedUrlForDownload = async (path: string, filename: string) => {
    const { data, error } = await supabase
      .storage
      .from("resources")
      // 60s de validité + "download" pour forcer Content-Disposition
      .createSignedUrl(path, 60, { download: filename });

    if (error || !data?.signedUrl) {
      console.error(error);
      return null;
    }
    return data.signedUrl;
  };

  const handleDownload = async (res: Resource) => {
    const url = await getSignedUrlForDownload(res.file_path, res.file_name);
    if (!url) { alert(t("resources:errors.sign_url")); return; }

    // iOS Safari aime les navigations "même onglet"
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isIOS) {
      window.location.assign(url);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_self";
      a.rel = "noopener";
      a.download = res.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  // Vue non-premium
  if (!isPremium) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 text-center shadow-sm">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Lock className="w-5 h-5" />
            <h2 className="text-lg font-semibold">{t("resources:premium.title")}</h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("resources:premium.body")}
          </p>
        </div>
      </div>
    );
  }

  // Helper d’affichage : chemin relatif (sans userId/)
  const getRelativePath = (p: string) => {
    const prefix = userId ? `${userId}/` : "";
    return p.startsWith(prefix) ? p.slice(prefix.length) : p;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      {/* ===== HEADER + ACTIONS STICKY ===== */}
      <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        {/* Top bar */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] flex items-center gap-3">
          <BackButton fallback="/dashboard" />
          <h1 className="text-lg sm:text-xl font-bold text-orange-600 dark:text-orange-400 inline-flex items-center gap-2">
            <Folder className="w-5 h-5" />
            {t("resources:title")}
          </h1>
        </div>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-3 flex flex-wrap items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
          <button
            className="inline-flex items-center gap-1 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 rounded px-1"
            onClick={() => goToPath(0)}
            title={t("resources:breadcrumb.root")}
          >
            <Home className="w-4 h-4" />
            {t("resources:breadcrumb.root")}
          </button>

          {crumbs.map((seg, i) => (
            <span key={`${seg}-${i}`} className="inline-flex items-center gap-1">
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <button
                className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 rounded px-1"
                onClick={() => goToPath(i + 1)}
                title={seg}
              >
                {seg}
              </button>
            </span>
          ))}
        </div>

        {/* Actions bar sticky (upload + folder + search) */}
        <div className="border-t border-gray-200 dark:border-gray-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {/* Choisir fichier */}
              <label
                className="cursor-pointer bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded inline-flex items-center gap-2
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 transition-colors"
                title={
                  selectedFile
                    ? t("resources:upload.change_file")
                    : t("resources:upload.choose_file")
                }
              >
                {selectedFile ? renderFileIcon(selectedFile.name) : <UploadCloud className="w-4 h-4" />}
                <span className="text-sm">
                  {selectedFile ? t("resources:upload.change_file") : t("resources:upload.choose_file")}
                </span>
                <input type="file" onChange={handleFileSelect} className="hidden" />
              </label>

              {/* Upload / Annuler */}
              {selectedFile && (
                <>
                  <button
                    onClick={handleUploadSelected}
                    disabled={uploading}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded inline-flex items-center gap-2 disabled:opacity-60
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 transition-colors"
                    title={t("resources:upload.upload_here_title", { path: `/${currentPath || ""}` })}
                  >
                    <UploadCloud className="w-4 h-4" />
                    <span className="text-sm">
                      {uploading ? t("resources:upload.uploading") : t("resources:upload.upload_here")}
                    </span>
                  </button>

                  <button
                    onClick={() => setSelectedFile(null)}
                    className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800
                             text-gray-700 dark:text-gray-200 px-3 py-2 rounded inline-flex items-center gap-2
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 transition-colors"
                    title={t("resources:upload.cancel_select_title")}
                  >
                    <X className="w-4 h-4" />
                    <span className="text-sm">{t("resources:upload.cancel")}</span>
                  </button>
                </>
              )}

              {/* Nouveau dossier */}
              <button
                onClick={createFolder}
                className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800
                         text-gray-700 dark:text-gray-200 px-3 py-2 rounded inline-flex items-center gap-2
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 transition-colors"
                title={t("resources:upload.create_folder_title", { path: `/${currentPath || ""}` })}
              >
                <FolderPlus className="w-4 h-4" />
                <span className="text-sm">{t("resources:upload.new_folder")}</span>
              </button>
            </div>

            {/* Infos fichier sélectionné (compact) */}
            {selectedFile && (
              <div className="text-xs text-gray-700 dark:text-gray-300 flex flex-wrap items-center gap-2">
                {renderFileIcon(selectedFile.name)}
                <span className="font-medium">{selectedFile.name}</span>
                <span className="text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024).toFixed(1)} {t("resources:upload.kb_suffix")}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {t("resources:upload.selected_target", { path: `/${currentPath || ""}` })}
                </span>
              </div>
            )}

            {/* Search */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("resources:search.placeholder")}
              aria-label={t("resources:search.label")}
              className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
                       placeholder-gray-400 dark:placeholder-gray-500 px-3 py-2 rounded w-full
                       focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors"
            />
          </div>
        </div>
      </div>

      {/* ===== CONTENU ===== */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        <div className="space-y-4">
          {/* Dossiers */}
          {folders.length > 0 && (
            <div>
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {t("resources:sections.folders")}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {folders.map((name) => (
                  <button
                    key={name}
                    onClick={() => enterFolder(name)}
                    className="group border border-gray-200 dark:border-gray-800 rounded-xl p-3 flex items-center gap-3 bg-white dark:bg-gray-900
                             hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
                    title={t("resources:folders.open_title", { name })}
                  >
                    <Folder className="w-5 h-5 text-amber-600 group-hover:scale-105 transition-transform" />
                    <span className="truncate">{name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fichiers */}
          <div>
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t("resources:sections.files")}
            </div>

            {files.length === 0 ? (
              <div className="text-gray-600 dark:text-gray-400 text-sm">
                {t("resources:sections.empty_files")}
              </div>
            ) : (
              <ul className="space-y-3">
                {files.map((res) => (
                  <li
                    key={res.id}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm rounded-xl p-3
                             flex justify-between items-center"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{renderFileIcon(res.file_name)}</div>
                      <div>
                        <div className="font-medium text-orange-600 dark:text-orange-400">
                          {res.file_name}
                        </div>
                        {res.description && (
                          <div className="text-sm text-gray-700 dark:text-gray-300 italic">
                            {res.description}
                          </div>
                        )}
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {new Date(res.created_at).toLocaleString(locale)} — /{getRelativePath(res.file_path)}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(res)}
                        className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1
                                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1"
                        title={t("resources:files.download_title")}
                        aria-label={t("resources:files.download_title")}
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(res)}
                        className="text-red-600 dark:text-red-400 hover:opacity-90
                                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded px-1"
                        title={t("resources:files.delete_title")}
                        aria-label={t("resources:files.delete_title")}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
